import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

type Level = {
  /** inclusive min */
  min: number;
  /** inclusive max */
  max: number;
  /** active/brighter color when this level is reached */
  color: string;
  /** light/faded color for the portion not yet reached */
  fillColor?: string;
};

type WheelProps = {
  /** current score */
  value: number;
  /** dynamic list of ranges */
  levels: Level[];
  /** global max; scale of the wheel (e.g., 1000) */
  maxScore: number;
  size?: number; // canvas size (px), default: 300 (ignored when width+height set)
  /** when set with height, use rectangular canvas so full arc fits (no clipping) */
  width?: number;
  /** when set with width, use rectangular canvas so full arc fits (no clipping) */
  height?: number;
  thickness?: number; // arc thickness, default: 16
  trackColor?: string; // background ring color, default: transparent
  /** bottom gap in degrees for the wheel opening, default: 120 */
  gapAngleDeg?: number;
  /** degrees of empty space BETWEEN adjacent level segments, default: 8 */
  segmentGapDeg?: number;
  /** animate to target value, default: 900 */
  animationMs?: number;
  /** draw round end marker, default: true */
  marker?: boolean;
};

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

function drawArc(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  start: number,
  end: number,
  color: string,
  width: number,
) {
  if (end <= start) return;
  ctx.beginPath();
  ctx.lineCap = 'round';
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.arc(cx, cy, r, start, end, false);
  ctx.stroke();
}

function polar(cx: number, cy: number, r: number, ang: number) {
  return { x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) };
}

// If a fillColor isn't provided, make a soft tint from the active color
function defaultFill(color: string) {
  // simple opacity blend over white using canvas alpha via rgba
  // attempt to parse hex #RRGGBB
  const m = color.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16);
  const g = parseInt(m.substring(2, 4), 16);
  const b = parseInt(m.substring(4, 6), 16);
  return `rgba(${Number.isNaN(r) ? 170 : r}, ${Number.isNaN(g) ? 170 : g}, ${Number.isNaN(b) ? 170 : b}, 0.25)`;
}

/**
 * TQ Score Wheel
 * - Dynamic Score Wheel
 * - Bottom-centered gap (vertical opening)
 * - Fully dynamic levels with per-level active color and inactive fillColor
 * - Configurable gap BETWEEN level segments
 * - Supports external maxScore (total scale)
 * - Animated value & round end marker colored by the active level
 * - TypeScript + Canvas (retina-safe)
 * @param props -
 * - value: number - current score
 * - levels: {
 *   min: number - inclusive min
 *   max: number - inclusive max
 *   color: string - active/brighter color when this level is reached
 *   fillColor?: string - light/faded color for the portion not yet reached
 * }[] - dynamic list of ranges
 * - maxScore: number - global max; scale of the wheel (e.g., 1000)
 * - size: number - canvas size (px), default: 300
 * - thickness: number - arc thickness, default: 16
 * - trackColor: string - background ring color, default: transparent
 * - gapAngleDeg: number - bottom gap in degrees for the wheel opening, default: 120
 * - segmentGapDeg: number - degrees of empty space BETWEEN adjacent level segments, default: 8
 * - animationMs: number - animate to target value, default: 900
 * - marker: boolean - draw round end marker, default: true
 * @example
 * <TQScoreWheel
 *   value={100}
 *   levels={levels}
 *   maxScore={1000}
 *   size={300}
 *   thickness={16}
 *   trackColor="transparent"
 *   gapAngleDeg={120}
 *   segmentGapDeg={8}
 *   animationMs={900}
 *   marker={true}
 * />
 */

const TQScoreWheel: React.FC<WheelProps> = ({
  value,
  levels,
  maxScore,
  size = 300,
  width: widthProp,
  height: heightProp,
  thickness = 16,
  trackColor = 'transparent',
  gapAngleDeg = 120,
  segmentGapDeg = 8,
  animationMs = 900,
  marker = true,
}) => {
  const isRect = widthProp != null && heightProp != null;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [animValue, setAnimValue] = useState(value);
  const deviceRatio =
    typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  // Resolve min from levels (default 0)
  const { minAll } = useMemo(() => {
    const mins = levels.map(l => l.min);
    return { minAll: Math.min(0, ...mins) };
  }, [levels]);

  // Animation to target value
  useEffect(() => {
    const start = animValue;
    const end = clamp(value, minAll, maxScore);
    if (animationMs <= 0 || Math.abs(end - start) < 0.5) {
      setAnimValue(end);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const ease = (t: number) => 1 - (1 - t) ** 3; // easeOutCubic
    const tick = (now: number) => {
      const p = clamp((now - t0) / animationMs, 0, 1);
      const v = start + (end - start) * ease(p);
      setAnimValue(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, minAll, maxScore, animationMs]);

  // Drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const thicknessPx = thickness * deviceRatio;
    let px: number;
    let py: number;
    let cx: number;
    let cy: number;
    let r: number;

    if (isRect && widthProp != null && heightProp != null) {
      // Rectangular canvas: fit full arc so both corners are visible (no clipping)
      const w = widthProp;
      const h = heightProp;
      const contentReserve = 48; // bottom space for score + badge overlay
      const topPadding = 6;
      // Arc vertical span = r + r/2 = 1.5r; leave room for stroke (thickness/2) at bottom
      const rCss =
        (h - contentReserve - topPadding - thickness / 2) / 1.5;
      r = Math.min(rCss, (w / 2) - 4) * deviceRatio; // cap by half-width, then to device px
      const rCssFinal = r / deviceRatio;
      cy = (topPadding + rCssFinal) * deviceRatio;
      cx = (w / 2) * deviceRatio;
      px = w * deviceRatio;
      py = h * deviceRatio;
      canvas.width = px;
      canvas.height = py;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    } else {
      const s = size * deviceRatio;
      px = s;
      py = s;
      canvas.width = px;
      canvas.height = py;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      cx = px / 2;
      cy = py / 2;
      r = (px - thicknessPx) / 2 - 4 * deviceRatio;
    }
    const gap = (gapAngleDeg * Math.PI) / 180; // bottom gap
    const segmentGap = (segmentGapDeg * Math.PI) / 180; // between segments

    // Wheel span (no bottom gap)
    const baseStart = Math.PI / 2 + gap / 2; // left of bottom
    const baseSweep = Math.PI * 2 - gap; // total usable angle

    // Compute level fractions across [minAll,maxScore]
    const total = maxScore - minAll;
    const L = levels.length;
    const totalInterGaps = Math.max(0, L - 1) * segmentGap; // angular space eaten by gaps
    const drawable = Math.max(0, baseSweep - totalInterGaps);

    // Helpers
    const portion = (lo: number, hi: number) =>
      clamp(hi, minAll, maxScore) - clamp(lo, minAll, maxScore);

    // Map value to angle respecting removed inter-segment gaps
    function segmentAngleSpan(lo: number, hi: number) {
      const frac = portion(lo, hi) / total;
      return drawable * frac;
    }

    // Build segments with explicit start/end angles including per-gap spacing
    const segments = levels.map((lv, idx) => {
      const sweepBefore = levels
        .slice(0, idx)
        .reduce(
          (acc, p, i) =>
            acc + segmentAngleSpan(p.min, p.max) + (i >= 0 ? segmentGap : 0),
          0,
        );
      const start = baseStart + sweepBefore;
      const span = segmentAngleSpan(lv.min, lv.max);
      const end = start + span;
      return { idx, ...lv, start, end };
    });

    // Clear
    ctx.clearRect(0, 0, px, py);

    // Soft shadow for depth
    ctx.save();
    // ctx.filter = `drop-shadow(0 ${2 * deviceRatio}px ${6 * deviceRatio}px rgba(0,0,0,0.08))`;

    // Background track
    drawArc(
      ctx,
      cx,
      cy,
      r,
      baseStart,
      baseStart + baseSweep,
      trackColor,
      thicknessPx,
    );

    // Draw each segment: first its inactive fill, then paint active up to current value
    const currentVal = clamp(animValue, minAll, maxScore);

    segments.forEach(seg => {
      const fill = seg.fillColor || defaultFill(seg.color);
      // Always draw the whole segment as inactive fill
      drawArc(
        ctx,
        cx,
        cy,
        r,
        seg.start,
        seg.end,
        fill,
        thicknessPx,
      );

      // Determine how much of this segment is active
      const segLo = seg.min;
      const segHi = seg.max;

      if (currentVal <= segLo) {
        // not reached
        return;
      }

      if (currentVal >= segHi) {
        // fully reached
        drawArc(
          ctx,
          cx,
          cy,
          r,
          seg.start,
          seg.end,
          seg.color,
          thicknessPx,
        );
      } else {
        // partially reached within this segment
        const p = (currentVal - segLo) / (segHi - segLo);
        const mid = seg.start + (seg.end - seg.start) * p;
        drawArc(
          ctx,
          cx,
          cy,
          r,
          seg.start,
          mid,
          seg.color,
          thicknessPx,
        );
      }
    });

    // Marker at current value (small ring at the active edge)
    if (marker) {
      // Find which segment contains currentVal to color the marker
      const activeSeg =
        segments.find(s => currentVal >= s.min && currentVal <= s.max) ||
        segments[segments.length - 1];

      let aVal: number;

      if (!activeSeg) {
        aVal = baseStart; // fallback
      } else {
        const frac = clamp(
          (currentVal - activeSeg.min) / (activeSeg.max - activeSeg.min),
          0,
          1,
        );
        aVal = activeSeg.start + (activeSeg.end - activeSeg.start) * frac;
      }

      const m = polar(cx, cy, r, aVal);
      const outer = thicknessPx * 0.58;
      const inner = thicknessPx * 0.34;

      ctx.beginPath();
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = activeSeg ? activeSeg.color : '#AAAAAA';
      ctx.lineWidth = 4 * deviceRatio;
      ctx.arc(m.x, m.y, outer, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      ctx.beginPath();
      ctx.fillStyle = '#FFFFFF';
      ctx.arc(m.x, m.y, inner, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    animValue,
    levels,
    maxScore,
    size,
    widthProp,
    heightProp,
    isRect,
    thickness,
    trackColor,
    gapAngleDeg,
    segmentGapDeg,
    deviceRatio,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className="block tq-score-wheel tq-score-wheel--canvas"
    />
  );
};

export default memo(TQScoreWheel);
